<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { api } from '../../api/client'
import type {
  NavDeltaData, EnrichedSynthNavData, SignalData, UserExecutionStatus,
} from '../../api/client'
import { FUND_CONFIG, type Fund } from './fundConfig'
import NavComparisonCard from './NavComparisonCard.vue'
import FvaCard from './FvaCard.vue'
import SettlementTimelineCard from './SettlementTimelineCard.vue'

const props = withDefaults(defineProps<{
  fund: Fund
  selectedDate: string
  isForwardLooking: boolean
  closureName?: string | null   // fund dealing-calendar closure for selectedDate (spec 2026-09-07 § 5)
  live?: boolean | null         // 2026-11-03: LIVE = the viewer's pot allocates the fund; DARK = not; null = unknown (no pill)
}>(), { closureName: undefined, live: null })   // explicit null: Vue would cast an ABSENT boolean prop to false (= a wrong DARK pill)

const livePill = computed(() => props.live == null ? null : props.live ? 'LIVE' : 'DARK')

const cfg = computed(() => FUND_CONFIG[props.fund])
const testIdRoot = computed(() => `fund-nav-block-${props.fund.toLowerCase()}`)
const testIdHeader = computed(() => `fund-nav-block-header-${props.fund.toLowerCase()}`)
const testIdBody = computed(() => `fund-nav-block-body-${props.fund.toLowerCase()}`)

const collapsed = ref(false)

const deltas = ref<NavDeltaData[]>([])
const enrichedNav = ref<EnrichedSynthNavData | null>(null)
const execStatus = ref<UserExecutionStatus | null>(null)
const signalData = ref<SignalData | null>(null)

let timer: ReturnType<typeof setInterval> | null = null

async function fetchAll() {
  const [ndR, enR, esR, sgR] = await Promise.allSettled([
    api.userNavDeltas(undefined, undefined, props.fund),
    props.isForwardLooking ? api.enrichedSynthNav(props.fund) : Promise.resolve(null),
    props.isForwardLooking ? api.userExecutionStatus(props.fund) : Promise.resolve(null),
    props.isForwardLooking ? api.signal(props.fund) : Promise.resolve(null),
  ])
  if (ndR.status === 'fulfilled') deltas.value = ndR.value ?? []
  if (enR.status === 'fulfilled') {
    const d = enR.value
    enrichedNav.value = (d && (d as EnrichedSynthNavData).available !== false) ? (d as EnrichedSynthNavData) : null
  }
  if (esR.status === 'fulfilled') execStatus.value = esR.value ?? null
  if (sgR.status === 'fulfilled') signalData.value = sgR.value ?? null
}

onMounted(() => {
  fetchAll()
  timer = setInterval(fetchAll, 30_000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

watch(() => [props.fund, props.selectedDate, props.isForwardLooking], () => {
  fetchAll()
})

const latestDelta = computed(() => {
  if (deltas.value.length === 0) return null
  return deltas.value.find(d => d.date === props.selectedDate) ?? null
})

// The strike column is declared per fund in FUND_CONFIG (spec 2026-09-30 §4.1) —
// never inferred from the label, which silently sent JAPAN to BGA's
// inferredBgNav10 column while its strikeShort read '08:40'.
const strikeNav = computed(() => latestDelta.value?.[cfg.value.strikeColumn] ?? null)

const officialNavValue = computed(() => latestDelta.value?.officialNav ?? null)
const synthVsOfficialBps = computed(() => latestDelta.value?.synthVsOfficialBps ?? null)
// Prefer the booked nav_deltas value (BGA's V060 residual booker; JH's
// per-fund fva:decision key writer is a follow-up). When the delta hasn't
// booked yet (JH today; BGA between publish and NAV strike), fall back to
// the live signal payload's fva_correction (fraction → bps) so the
// operator sees the same number the engine actually applied. Residual
// has no signal-payload equivalent — stays null until booking runs.
// Shadow-only corrections still route through the booker for BGA;
// JH doesn't have a shadow path yet.
const fvaPredictedBps = computed(() => {
  const booked = latestDelta.value?.fvaPredictedBps
  if (booked != null) return booked
  const corr = signalData.value?.fva_correction
  if (corr != null) return corr * 10000
  // Publish-only shadow (JH's whole path today — live mode is force-parked;
  // also BGA's shadow between publish and booking). Fraction → bps.
  const shadow = signalData.value?.fva_shadow_correction
  return shadow != null ? shadow * 10000 : null
})
const fvaResidualBps = computed(() => latestDelta.value?.fvaResidualBps ?? null)
// Feature behind the prediction, for the (i) popover — shadow m_t when the
// shadow computed it (JH's path), applied m_t otherwise. Fraction → bps.
const fvaMtBps = computed(() => {
  const m = signalData.value?.fva_shadow_m_t ?? signalData.value?.fva_m_t
  return m != null ? m * 10000 : null
})
const fvaShadow = computed(() => {
  if (latestDelta.value?.fvaPredictedBps != null)
    return latestDelta.value?.fvaShadow ?? false
  // An applied payload correction is not shadow; only the shadow-field
  // fallback earns the "(shadow)" label.
  if (signalData.value?.fva_correction != null) return false
  if (signalData.value?.fva_shadow_correction != null) return true
  return latestDelta.value?.fvaShadow ?? false
})

const baseNavDate = computed(() => enrichedNav.value?.prev_official_nav_date ?? null)

// Base NAV VALUE for the headline % changes: the latest row strictly before
// the selected date that actually struck an official NAV. Ratio of same-unit
// row values, so no pence/pounds conversion is involved. Works for both the
// live (forward-looking) date and historical selections.
const baseNavValue = computed(() => {
  const prior = deltas.value
    .filter(d => d.date < props.selectedDate && d.officialNav != null)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
  return prior[0]?.officialNav ?? null
})

// Source-of-truth is the selected date's nav_deltas row. Previously this
// consulted /api/nav/{fund}'s in-memory LatestNavPence, which:
//   - only reflected the latest LIVE poll (not the selected historical date), and
//   - was null after any API restart on a non-trading day until the next
//     successful poll — GGE surfaced this on 2026-08-22 (Sat) after redeploy:
//     Fri 21 Aug had a fully-populated nav_deltas row yet the pill read
//     "No NAV data" because the Saturday /api/nav/gge returned available:false.
// The row is populated by the same BookOfficialNavAsync path the endpoint's
// in-memory flag comes from, so no forward-latency regression.
const hasOfficialNav = computed(() => latestDelta.value?.officialNav != null)

const statusPillLabel = computed(() => {
  if (props.closureName) return `Fund closed · ${props.closureName}`
  if (hasOfficialNav.value) return 'NAV received'
  if (props.isForwardLooking) return 'Awaiting NAV'
  return 'No NAV data'
})

const statusPillClass = computed(() => {
  if (props.closureName) return 'status-grey'
  if (hasOfficialNav.value) return 'status-green'
  if (props.isForwardLooking) return 'status-amber'
  return 'status-grey'
})

const strikeLabel = computed(() => `~${cfg.value.strikeTime} UK`)

const execStatusMapped = computed<'executed' | 'hold' | 'pending' | null>(() => {
  const s = execStatus.value?.status
  if (s === 'executed' || s === 'hold') return s
  if (s === 'skipped' || s == null) return 'pending'
  return 'pending'
})

</script>

<template>
  <div class="fund-nav-block" :data-testid="testIdRoot" :data-fund="fund">
    <div class="fnb-header" :data-testid="testIdHeader" @click="collapsed = !collapsed">
      <span class="fnb-caret">{{ collapsed ? '▸' : '▾' }}</span>
      <span class="fnb-title">{{ fund }} NAV</span>
      <span v-if="livePill" class="fnb-live-pill" :class="livePill.toLowerCase()" data-testid="fund-nav-live-pill"
            :title="live ? 'In your pot (weight > 0)' : 'Not in your pot — synthetic NAV still shown'">{{ livePill }}</span>
      <span class="fnb-status-pill" :class="statusPillClass">● {{ statusPillLabel }}</span>
    </div>
    <div v-if="!collapsed" class="fnb-body" :data-testid="testIdBody">
      <NavComparisonCard
        :fund="fund"
        :synthetic-nav="strikeNav"
        :official-nav="officialNavValue"
        :synth-vs-official-bps="synthVsOfficialBps"
        :is-forward-looking="isForwardLooking"
        :base-nav-date="baseNavDate"
        :base-nav="baseNavValue"
      />
      <FvaCard
        :fund="fund"
        :fva-predicted-bps="fvaPredictedBps"
        :fva-residual-bps="fvaResidualBps"
        :fva-shadow="fvaShadow"
        :fva-mt-bps="fvaMtBps"
        :decision="signalData?.decision ?? null"
        :exec-status="execStatusMapped"
      />
      <SettlementTimelineCard
        :fund="fund"
        :signal-time="signalData?.timestamp ?? null"
        :exec-status="execStatusMapped"
        :exec-time="execStatus?.executedAt ?? null"
        :has-official-nav="hasOfficialNav"
        :strike-label="strikeLabel"
      />
    </div>
  </div>
</template>

<style scoped>
.fund-nav-block {
  display: flex;
  flex-direction: column;
  border: 1px solid #1a1a1a;
  border-radius: 6px;
  background: #0a0a0a;
}
.fnb-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  background: #111;
  border-bottom: 1px solid #1a1a1a;
  border-radius: 6px 6px 0 0;
}
.fnb-caret { color: #666; font-size: 0.75rem; width: 12px; }
.fnb-title { flex: 1; font-size: 0.85rem; color: #ccc; letter-spacing: 1px; }
/* LIVE / DARK pill — same look as the FOF home pills */
.fnb-live-pill { display: inline-block; padding: 1px 7px; margin-right: 8px; border-radius: 9px; font-size: 10px; font-weight: 700; letter-spacing: .3px; line-height: 15px; }
.fnb-live-pill.live { background: #1f3a5a; color: #6ea8df; }
.fnb-live-pill.dark { background: #2a2a2a; color: #9e9e9e; border: 1px solid #444; }
.fnb-status-pill {
  font-size: 0.7rem; letter-spacing: 0.5px;
  padding: 2px 8px; border-radius: 10px;
}
.status-green { color: #4caf50; background: #0a1a0a; }
.status-amber { color: #ff9800; background: #1a1400; }
.status-grey  { color: #666;    background: #0a0a0a; border: 1px solid #222; }
.fnb-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
}
@media (max-width: 768px) {
  .fnb-title { font-size: 0.75rem; }
  .fnb-status-pill { font-size: 0.65rem; }
}
</style>
